<template>
  <transition :name="transitionName">
    <div
      v-show="value"
      ref="drawer"
      aria-labelledby="drawer-title"
      aria-describedby="drawer-description"
      class="idf-drawer"
      data-test-drawer-wrapper
      role="dialog"
      tabindex="0"
      :style="{ '--idf-drawer-height': height, '--idf-drawer-bg-color': bgColor }"
      @click.self="onClose"
      @keyup.esc="onClose"
    >
      <div class="idf-drawer__dialog" :class="[`idf-drawer__dialog--${direction}`]" role="document">
        <div
          class="idf-drawer__content"
          :class="[
            `idf-drawer__content--${direction}`,
            {
              'idf-drawer__content--no-spacings': noSpacings,
            },
          ]"
        >
          <div class="idf-drawer__header" :class="{ 'idf-drawer__header--no-spacings': noSpacings }">
            <div id="drawer-title" class="idf-drawer__header-content">
              <!-- @slot Slot pour ajouter du contenu dans le header -->
              <slot name="header" />
            </div>
            <button
              v-if="showCloseButton"
              ref="drawerClose"
              aria-label="Fermer"
              class="idf-drawer__close"
              data-test-drawer-close
              type="button"
              @click="onClose"
            >
              <span class="adn-icon adn-icon--close"></span>
            </button>
          </div>
          <div id="drawer-description" class="idf-drawer__body">
            <!-- @slot Slot pour ajouter du contenu dans le body -->
            <slot name="body" />
          </div>
          <div v-if="hasFooterSlot" class="idf-drawer__footer">
            <!-- @slot Slot pour ajouter du contenu dans le footer -->
            <slot name="footer" />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { computed, defineComponent, ref, watch } from 'vue'
import { IdfDrawerDirection } from './IdfDrawer.model'

export default defineComponent({
  name: 'IdfDrawer',
  props: {
    direction: {
      type: String as PropType<IdfDrawerDirection>,
      default: IdfDrawerDirection.RTL,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
    },
    value: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: 'fit-content',
    },
    bgColor: {
      type: String,
      default: '#FFFFFF',
    },
    noSpacings: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'update:modelValue'],
  setup(props, { slots, emit }) {
    const hasFooterSlot = computed(() => Boolean(slots.footer))
    const transitionName = computed(() => `${props.direction}`)
    const drawer = ref<HTMLDivElement | null>(null)

    watch(
      () => props.value,
      (newValue) => {
        return newValue
          ? document.body.classList.add('idf-drawer-open')
          : document.body.classList.remove('idf-drawer-open')
      },
    )

    watch(
      () => drawer.value,
      (newValue) => {
        return newValue && newValue.focus()
      },
    )

    const onClose = (): void => {
      emit('close')
      emit('update:modelValue', false)
    }

    return { hasFooterSlot, transitionName, onClose, drawer }
  },
})
</script>

<style lang="scss">
body.idf-drawer-open {
  overflow: hidden;
}
</style>

<style lang="scss" scoped>
$transition-duration: 0.35s;

.btt-enter-active,
.btt-leave-active,
.ttb-enter-active,
.ttb-leave-active,
.ltr-enter-active,
.ltr-leave-active,
.rtl-enter-active,
.rtl-leave-active {
  transition: all $transition-duration ease;

  .idf-drawer__dialog {
    transition: all $transition-duration ease;
  }
}

.rtl-enter,
.rtl-leave-to {
  opacity: 0;

  .idf-drawer__dialog {
    transform: translate3d(100vw, 0, 0);
  }
}

.ltr-enter,
.ltr-leave-to {
  opacity: 0;

  .idf-drawer__dialog {
    transform: translate3d(-100vw, 0, 0);
  }
}

.ttb-enter,
.ttb-leave-to {
  opacity: 0;

  .idf-drawer__dialog {
    transform: translate3d(0, -80vh, 0);
  }
}

.btt-enter,
.btt-leave-to {
  opacity: 0;

  .idf-drawer__dialog {
    transform: translate3d(0, 80vh, 0);
  }
}

.idf-drawer {
  background: rgb(56 56 58 / 50%);
  inset: 0;
  outline: 0;
  overflow: hidden;
  position: fixed;
  z-index: 100;

  &__dialog {
    max-width: 100vw;
    position: fixed;
    transform: translate3d(0, 0, 0);

    &--rtl {
      right: 0;
    }

    &--ltr {
      left: 0;
    }

    &--ttb {
      top: 0;
    }

    &--btt {
      bottom: 0;
    }
  }

  &__content {
    background-color: var(--idf-drawer-bg-color);
    box-shadow: var(--shadow)-dark;
    color: #000438;
    display: flex;
    flex-direction: column;
    padding: var(--spacing-5);
    position: relative;
    width: 100%;

    &--no-spacings {
      padding: var(--spacing-4);
    }

    &--rtl,
    &--ltr {
      height: 100vh;
      max-width: calc(100vw - var(--spacing-3) * 2);
    }

    &--btt,
    &--ttb {
      height: var(--idf-drawer-height);
      max-height: calc(100dvh - var(--spacing-5) * 2);
      width: 100vw;
    }

    &--rtl {
      border-top-left-radius: var(--border-radius-m);
      border-bottom-left-radius: var(--border-radius-m);
    }

    &--ltr {
      border-top-right-radius: var(--border-radius-m);
      border-bottom-right-radius: var(--border-radius-m);
    }

    &--btt {
      border-top-left-radius: var(--border-radius-m);
      border-top-right-radius: var(--border-radius-m);
    }

    &--ttb {
      border-bottom-left-radius: var(--border-radius-m);
      border-bottom-right-radius: var(--border-radius-m);
    }
  }

  &__header {
    border-bottom: 2px solid #ccd1d3;
    display: flex;
    margin-bottom: var(--spacing-3);
    padding-top: var(--spacing-2);
    padding-bottom: var(--spacing-3);

    &-content {
      width: 100%;
      padding-right: var(--spacing-2);
    }

    &--no-spacings {
      padding: 0;
      margin: 0;
    }
  }

  &__close {
    @include idf-reset-button;

    padding: 2rem;
    position: absolute;
    right: 0;
    top: 0;

    .icon path {
      fill: #cacad5;
    }
  }

  &__body {
    max-height: 75vh;
    overflow: auto;
    margin-top: var(--spacing-2);
    padding-left: var(--spacing-2);
    scrollbar-width: thin;
    scrollbar-color: #cacad5 #eaeaf2;

    &::-webkit-scrollbar {
      width: var(--spacing-1);
    }

    &::-webkit-scrollbar-track {
      background: #eaeaf2;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #cacad5;
      border: 0.1rem solid #cacad5;
    }
  }

  &__footer {
    display: flex;
    margin-top: var(--spacing-5);
  }
}

@include idf-media-up(md) {
  .idf-drawer {
    &__content {
      &--rtl,
      &--ltr {
        max-width: calc(40vw - var(--spacing-5) * 2);
      }

      &--btt {
        max-width: 100vw;
        width: 100vw;
        height: var(--idf-drawer-height);
      }

      &--ttb {
        max-width: 100vw;
        width: 100vw;
        height: var(--idf-drawer-height);
      }
    }

    &__body {
      max-height: 100%;
    }
  }
}
</style>
